import { useState, useMemo } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ResourceCard from './components/ResourceCard';

export default function ResourcesHub() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { label: 'All Resources', value: 'All', icon: 'ri-apps-line' },
    { label: 'Study & Devotion', value: 'Study', icon: 'ri-book-open-line' },
    { label: 'Music & Worship', value: 'Music', icon: 'ri-music-2-line' },
    { label: 'Stewardship', value: 'Stewardship', icon: 'ri-hand-coin-line' },
    { label: 'Leadership', value: 'Leadership', icon: 'ri-team-line' },
    { label: 'Policy', value: 'Policy', icon: 'ri-file-list-line' },
    { label: 'Bible Apps', value: 'Bible', icon: 'ri-smartphone-line' },
    { label: 'Digital Systems', value: 'Digital', icon: 'ri-cloud-line' },
    { label: 'Media & Identity', value: 'Media', icon: 'ri-image-line' }
  ];

  // Subcategories with their resources
  const subcategories = {
    Study: [
      {
        name: 'Sabbath School Bible Study Guides',
        description: 'Quarterly Bible study lessons for all age groups',
        icon: 'ri-book-open-line',
        color: 'from-blue-500 to-indigo-600',
        resources: [
          {
            name: 'Adult Sabbath School Bible Study Guide',
            description: 'Quarterly Bible study lessons for adult Sabbath School classes',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-book-open-line',
            color: 'from-blue-500 to-indigo-600',
            category: 'Study',
            link: 'https://www.absg.adventist.org/'
          },
          {
            name: 'Youth Sabbath School Study Guide',
            description: 'Engaging Bible study materials designed for youth',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-user-star-line',
            color: 'from-purple-500 to-purple-600',
            category: 'Study',
            link: 'https://www.absg.adventist.org/'
          },
          {
            name: 'Children\'s Sabbath School Materials',
            description: 'Age-appropriate Bible study resources for children',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-bear-smile-line',
            color: 'from-pink-500 to-rose-600',
            category: 'Study',
            link: 'https://www.gracelink.net/'
          }
        ]
      },
      {
        name: 'Daily Devotionals',
        description: 'Daily spiritual reflections and Bible readings',
        icon: 'ri-heart-3-line',
        color: 'from-emerald-500 to-teal-600',
        resources: [
          {
            name: 'Adult Daily Devotional',
            description: 'Daily spiritual reflections and Bible readings',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-heart-3-line',
            color: 'from-emerald-500 to-teal-600',
            category: 'Study',
            link: 'https://www.revivalandreformation.org/'
          },
          {
            name: 'Youth Daily Devotional',
            description: 'Inspiring daily devotions for young people',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-flashlight-line',
            color: 'from-orange-500 to-amber-600',
            category: 'Study',
            link: 'https://www.revivalandreformation.org/'
          },
          {
            name: 'Family Worship Resources',
            description: 'Materials to strengthen family devotional time',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-home-heart-line',
            color: 'from-cyan-500 to-blue-600',
            category: 'Study',
            link: 'https://family.adventist.org/'
          },
          {
            name: 'Children\'s Devotional Materials',
            description: 'Age-appropriate devotional content for children',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-bear-smile-line',
            color: 'from-pink-500 to-pink-600',
            category: 'Study',
            link: 'https://children.adventist.org/'
          }
        ]
      },
      {
        name: 'Mission Readings & Stories',
        description: 'Quarterly mission stories and global mission updates',
        icon: 'ri-earth-line',
        color: 'from-teal-500 to-cyan-600',
        resources: [
          {
            name: 'Mission Quarterly',
            description: 'Stories from the mission field around the world',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-earth-line',
            color: 'from-teal-500 to-cyan-600',
            category: 'Study',
            link: 'https://www.adventistmission.org/'
          },
          {
            name: 'Children\'s Mission Stories',
            description: 'Mission stories adapted for children',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-map-pin-user-line',
            color: 'from-cyan-500 to-blue-600',
            category: 'Study',
            link: 'https://www.adventistmission.org/'
          }
        ]
      },
      {
        name: 'Week of Prayer Materials',
        description: 'Annual Week of Prayer resources and readings',
        icon: 'ri-hand-heart-line',
        color: 'from-purple-500 to-indigo-600',
        resources: [
          {
            name: 'Week of Prayer Readings',
            description: 'Official Week of Prayer devotional materials',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-hand-heart-line',
            color: 'from-purple-500 to-indigo-600',
            category: 'Study',
            link: 'https://www.revivalandreformation.org/'
          }
        ]
      },
      {
        name: 'Spiritual Growth & Bible Reading Plans',
        description: 'Structured Bible reading and spiritual growth resources',
        icon: 'ri-book-mark-line',
        color: 'from-indigo-500 to-purple-600',
        resources: [
          {
            name: 'Bible Reading Plans',
            description: 'Guided plans for reading through the Bible',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-book-mark-line',
            color: 'from-indigo-500 to-purple-600',
            category: 'Study',
            link: 'https://www.revivalandreformation.org/'
          },
          {
            name: 'Spiritual Growth Resources',
            description: 'Materials for deepening your walk with God',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-seedling-line',
            color: 'from-green-500 to-emerald-600',
            category: 'Study',
            link: 'https://www.revivalandreformation.org/'
          }
        ]
      }
    ],
    Music: [
      {
        name: 'SDAH Hymnal',
        description: 'Complete Seventh-day Adventist Hymnal in multiple formats',
        icon: 'ri-music-2-line',
        color: 'from-emerald-500 to-emerald-600',
        resources: [
          {
            name: 'SDA Hymnal - PDF',
            description: 'Complete Seventh-day Adventist Hymnal in PDF format',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-music-2-line',
            color: 'from-emerald-500 to-emerald-600',
            category: 'Music',
            formats: ['PDF']
          },
          {
            name: 'SDA Hymnal - PowerPoint Slides',
            description: 'Hymnal lyrics formatted for worship presentations',
            type: 'PPTX',
            source: 'Local Resource',
            accessMode: 'Download',
            icon: 'ri-slideshow-line',
            color: 'from-blue-500 to-blue-600',
            category: 'Music',
            formats: ['PPTX']
          },
          {
            name: 'SDA Hymnal - EasyWorship Format',
            description: 'Hymnal database for EasyWorship presentation software',
            type: 'Software',
            source: 'Local Resource',
            accessMode: 'Download',
            icon: 'ri-computer-line',
            color: 'from-purple-500 to-purple-600',
            category: 'Music',
            formats: ['EasyWorship']
          },
          {
            name: 'SDA Hymnal - OpenLP Format',
            description: 'Hymnal database for OpenLP presentation software',
            type: 'Software',
            source: 'Local Resource',
            accessMode: 'Download',
            icon: 'ri-tv-line',
            color: 'from-indigo-500 to-indigo-600',
            category: 'Music',
            formats: ['OpenLP']
          },
          {
            name: 'SDA Hymnal - OBS Format',
            description: 'Hymnal overlay for OBS streaming software',
            type: 'Software',
            source: 'Local Resource',
            accessMode: 'Download',
            icon: 'ri-live-line',
            color: 'from-rose-500 to-rose-600',
            category: 'Music',
            formats: ['OBS']
          }
        ]
      },
      {
        name: 'Responsive Readings & Calls to Worship',
        description: 'Scripture readings and worship invitations',
        icon: 'ri-file-text-line',
        color: 'from-teal-500 to-teal-600',
        resources: [
          {
            name: 'Responsive Readings Collection',
            description: 'Scripture readings for congregational worship',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-file-text-line',
            color: 'from-teal-500 to-teal-600',
            category: 'Music',
            formats: ['PDF']
          },
          {
            name: 'Calls to Worship',
            description: 'Opening worship invitations and liturgies',
            type: 'PDF',
            source: 'Local Resource',
            accessMode: 'Download',
            icon: 'ri-hand-heart-line',
            color: 'from-cyan-500 to-cyan-600',
            category: 'Music',
            formats: ['PDF']
          }
        ]
      },
      {
        name: 'Worship Service Orders & Templates',
        description: 'Templates for planning and conducting worship services',
        icon: 'ri-file-list-2-line',
        color: 'from-blue-500 to-indigo-600',
        resources: [
          {
            name: 'Worship Service Order Templates',
            description: 'Customizable templates for worship planning',
            type: 'PDF',
            source: 'Local Resource',
            accessMode: 'Download',
            icon: 'ri-file-list-2-line',
            color: 'from-blue-500 to-indigo-600',
            category: 'Music',
            formats: ['PDF', 'DOCX']
          }
        ]
      },
      {
        name: 'Choir & Special Music Resources',
        description: 'Resources for choir directors and special music ministry',
        icon: 'ri-music-line',
        color: 'from-purple-500 to-purple-600',
        resources: [
          {
            name: 'Choir Music Library',
            description: 'Collection of approved choir arrangements',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-music-line',
            color: 'from-purple-500 to-purple-600',
            category: 'Music',
            formats: ['PDF']
          }
        ]
      }
    ],
    Stewardship: [
      {
        name: 'Digital Giving Platforms',
        description: 'Official church stewardship systems',
        icon: 'ri-hand-coin-line',
        color: 'from-emerald-500 to-emerald-600',
        resources: [
          {
            name: 'Titan Stewardship System',
            description: 'Official digital giving platform for SDA churches',
            type: 'Web',
            source: 'Conference System',
            accessMode: 'External Link',
            icon: 'ri-hand-coin-line',
            color: 'from-emerald-500 to-emerald-600',
            category: 'Stewardship',
            link: 'https://www.titansystem.org/',
            note: 'External Stewardship System – Managed by the Church'
          },
          {
            name: '7me Giving Platform',
            description: 'Mobile and web-based church giving solution',
            type: 'Web',
            source: 'Conference System',
            accessMode: 'External Link',
            icon: 'ri-smartphone-line',
            color: 'from-blue-500 to-blue-600',
            category: 'Stewardship',
            link: 'https://www.7me.org/',
            note: 'External Stewardship System – Managed by the Church'
          },
          {
            name: 'Conference Giving Portal',
            description: 'Conference-approved online giving system',
            type: 'Web',
            source: 'Conference System',
            accessMode: 'External Link',
            icon: 'ri-bank-card-line',
            color: 'from-purple-500 to-purple-600',
            category: 'Stewardship',
            note: 'External Stewardship System – Managed by the Church'
          }
        ]
      },
      {
        name: 'Stewardship Manuals & Guides',
        description: 'Educational resources on biblical stewardship',
        icon: 'ri-book-2-line',
        color: 'from-blue-500 to-indigo-600',
        resources: [
          {
            name: 'Stewardship Manual',
            description: 'Comprehensive guide to biblical stewardship principles',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-book-2-line',
            color: 'from-blue-500 to-indigo-600',
            category: 'Stewardship',
            link: 'https://stewardship.adventist.org/'
          }
        ]
      },
      {
        name: 'Offering Promotion Materials',
        description: 'Resources for promoting special offerings and appeals',
        icon: 'ri-gift-line',
        color: 'from-orange-500 to-amber-600',
        resources: [
          {
            name: 'Offering Promotion Toolkit',
            description: 'Materials for promoting church offerings',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-gift-line',
            color: 'from-orange-500 to-amber-600',
            category: 'Stewardship',
            link: 'https://stewardship.adventist.org/'
          }
        ]
      },
      {
        name: 'Financial Education Resources',
        description: 'Tools for personal and family financial management',
        icon: 'ri-line-chart-line',
        color: 'from-teal-500 to-cyan-600',
        resources: [
          {
            name: 'Financial Peace Resources',
            description: 'Biblical principles for financial wellness',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-line-chart-line',
            color: 'from-teal-500 to-cyan-600',
            category: 'Stewardship',
            link: 'https://stewardship.adventist.org/'
          }
        ]
      }
    ],
    Leadership: [
      {
        name: 'Church Manual',
        description: 'Official guide for church governance and operations',
        icon: 'ri-book-line',
        color: 'from-slate-600 to-slate-700',
        resources: [
          {
            name: 'Church Manual',
            description: 'Official guide for church governance and operations',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-book-line',
            color: 'from-slate-600 to-slate-700',
            category: 'Leadership',
            version: '20th Edition',
            link: 'https://www.adventist.org/official-statements/church-manual/'
          }
        ]
      },
      {
        name: 'Elders\' Manual',
        description: 'Comprehensive guide for church elders',
        icon: 'ri-user-star-line',
        color: 'from-blue-600 to-indigo-700',
        resources: [
          {
            name: 'Elders\' Manual',
            description: 'Comprehensive guide for church elders',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-user-star-line',
            color: 'from-blue-600 to-indigo-700',
            category: 'Leadership',
            link: 'https://www.adventist.org/'
          }
        ]
      },
      {
        name: 'Pastors\' Manual',
        description: 'Resource guide for pastoral ministry',
        icon: 'ri-user-heart-line',
        color: 'from-emerald-600 to-teal-700',
        resources: [
          {
            name: 'Pastors\' Manual',
            description: 'Resource guide for pastoral ministry',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-user-heart-line',
            color: 'from-emerald-600 to-teal-700',
            category: 'Leadership',
            link: 'https://www.adventist.org/'
          }
        ]
      },
      {
        name: 'Deacons & Deaconesses Manual',
        description: 'Guidelines for deacon and deaconess ministry',
        icon: 'ri-service-line',
        color: 'from-cyan-600 to-blue-700',
        resources: [
          {
            name: 'Deacons & Deaconesses Manual',
            description: 'Guidelines for deacon and deaconess ministry',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-service-line',
            color: 'from-cyan-600 to-blue-700',
            category: 'Leadership',
            link: 'https://www.adventist.org/'
          }
        ]
      },
      {
        name: 'Ministry Department Manuals',
        description: 'Guidelines for specific ministry areas',
        icon: 'ri-team-line',
        color: 'from-purple-600 to-purple-700',
        resources: [
          {
            name: 'Youth Ministries Manual',
            description: 'Guidelines for youth ministry leadership',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-team-line',
            color: 'from-purple-600 to-purple-700',
            category: 'Leadership',
            link: 'https://youth.adventist.org/'
          },
          {
            name: 'Music Ministries Manual',
            description: 'Standards and practices for church music',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-music-line',
            color: 'from-rose-600 to-rose-700',
            category: 'Leadership',
            link: 'https://www.adventist.org/'
          },
          {
            name: 'Children\'s Ministries Manual',
            description: 'Guide for children\'s ministry programs',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-bear-smile-line',
            color: 'from-pink-600 to-pink-700',
            category: 'Leadership',
            link: 'https://children.adventist.org/'
          },
          {
            name: 'Women\'s Ministries Manual',
            description: 'Resources for women\'s ministry leadership',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-user-smile-line',
            color: 'from-cyan-600 to-cyan-700',
            category: 'Leadership',
            link: 'https://women.adventist.org/'
          },
          {
            name: 'Men\'s Ministries Manual',
            description: 'Guidelines for men\'s ministry programs',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-user-line',
            color: 'from-orange-600 to-orange-700',
            category: 'Leadership',
            link: 'https://www.adventist.org/'
          },
          {
            name: 'Sabbath School & Personal Ministries Manual',
            description: 'Comprehensive guide for Sabbath School leadership',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-book-open-line',
            color: 'from-teal-600 to-teal-700',
            category: 'Leadership',
            link: 'https://www.adventist.org/'
          }
        ]
      }
    ],
    Policy: [
      {
        name: 'Working Policy',
        description: 'General Conference Working Policy documents',
        icon: 'ri-file-list-3-line',
        color: 'from-gray-600 to-gray-700',
        resources: [
          {
            name: 'Working Policy (Public Extracts)',
            description: 'Public sections of the General Conference Working Policy',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-file-list-3-line',
            color: 'from-gray-600 to-gray-700',
            category: 'Policy',
            note: 'Issued by Global Church'
          }
        ]
      },
      {
        name: 'Official Statements & Guidelines',
        description: 'Church positions on contemporary issues',
        icon: 'ri-article-line',
        color: 'from-blue-600 to-blue-700',
        resources: [
          {
            name: 'Official Statements & Guidelines',
            description: 'Church positions on contemporary issues',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-article-line',
            color: 'from-blue-600 to-blue-700',
            category: 'Policy',
            link: 'https://www.adventist.org/official-statements/',
            note: 'Issued by Global Church'
          }
        ]
      },
      {
        name: 'Constitutions & By-Laws',
        description: 'Organizational constitutions and by-laws',
        icon: 'ri-file-shield-line',
        color: 'from-indigo-600 to-purple-700',
        resources: [
          {
            name: 'Division Constitution & By-Laws',
            description: 'Division-level organizational documents',
            type: 'PDF',
            source: 'Division',
            accessMode: 'Download',
            icon: 'ri-file-shield-line',
            color: 'from-indigo-600 to-purple-700',
            category: 'Policy',
            note: 'Issued by Division'
          },
          {
            name: 'Union Constitution & By-Laws',
            description: 'Union-level organizational documents',
            type: 'PDF',
            source: 'Union',
            accessMode: 'Download',
            icon: 'ri-file-shield-line',
            color: 'from-purple-600 to-purple-700',
            category: 'Policy',
            note: 'Issued by Union'
          }
        ]
      },
      {
        name: 'Public Circulars & Notices',
        description: 'Official communications and announcements',
        icon: 'ri-mail-line',
        color: 'from-emerald-600 to-emerald-700',
        resources: [
          {
            name: 'Division Circulars & Notices',
            description: 'Official communications from Division leadership',
            type: 'PDF',
            source: 'Division',
            accessMode: 'Download',
            icon: 'ri-mail-line',
            color: 'from-emerald-600 to-emerald-700',
            category: 'Policy',
            note: 'Issued by Division'
          },
          {
            name: 'Union Policy Documents',
            description: 'Union-level governance and policy materials',
            type: 'PDF',
            source: 'Union',
            accessMode: 'Download',
            icon: 'ri-file-text-line',
            color: 'from-purple-600 to-purple-700',
            category: 'Policy',
            note: 'Issued by Union'
          },
          {
            name: 'Conference Guidelines',
            description: 'Conference-specific policies and procedures',
            type: 'PDF',
            source: 'Conference',
            accessMode: 'Download',
            icon: 'ri-folder-line',
            color: 'from-orange-600 to-orange-700',
            category: 'Policy',
            note: 'Issued by Conference'
          }
        ]
      },
      {
        name: 'Safeguarding & Child Protection Policies',
        description: 'Policies for protecting children and vulnerable persons',
        icon: 'ri-shield-check-line',
        color: 'from-rose-600 to-pink-700',
        resources: [
          {
            name: 'Child Protection Policy',
            description: 'Guidelines for safeguarding children in church programs',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-shield-check-line',
            color: 'from-rose-600 to-pink-700',
            category: 'Policy',
            note: 'Issued by Global Church'
          },
          {
            name: 'Safeguarding Guidelines',
            description: 'Comprehensive safeguarding policies and procedures',
            type: 'PDF',
            source: 'Division',
            accessMode: 'Download',
            icon: 'ri-shield-user-line',
            color: 'from-pink-600 to-rose-700',
            category: 'Policy',
            note: 'Issued by Division'
          }
        ]
      }
    ],
    Bible: [
      {
        name: 'English Bible Apps',
        description: 'Bible applications in English',
        icon: 'ri-global-line',
        color: 'from-blue-600 to-blue-700',
        resources: [
          {
            name: 'YouVersion Bible App',
            description: 'Popular Bible app with multiple translations',
            type: 'App',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-smartphone-line',
            color: 'from-blue-600 to-blue-700',
            category: 'Bible',
            languages: ['English'],
            platforms: ['Android', 'iOS', 'Web'],
            link: 'https://www.bible.com/'
          },
          {
            name: 'Bible Gateway',
            description: 'Comprehensive online Bible study platform',
            type: 'Web',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-global-line',
            color: 'from-emerald-600 to-teal-700',
            category: 'Bible',
            languages: ['English'],
            platforms: ['Web'],
            link: 'https://www.biblegateway.com/'
          },
          {
            name: 'Blue Letter Bible',
            description: 'In-depth Bible study tools and resources',
            type: 'Web',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-book-open-line',
            color: 'from-indigo-600 to-indigo-700',
            category: 'Bible',
            languages: ['English'],
            platforms: ['Web', 'Android', 'iOS'],
            link: 'https://www.blueletterbible.org/'
          }
        ]
      },
      {
        name: 'Yorùbá Bible Apps',
        description: 'Bible applications in Yorùbá language',
        icon: 'ri-book-line',
        color: 'from-purple-600 to-purple-700',
        resources: [
          {
            name: 'Yorùbá Bible App',
            description: 'Dedicated Yorùbá language Bible application',
            type: 'App',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-book-line',
            color: 'from-purple-600 to-purple-700',
            category: 'Bible',
            languages: ['Yorùbá'],
            platforms: ['Android', 'iOS'],
            link: 'https://play.google.com/store/apps/details?id=com.yorubabible'
          },
          {
            name: 'YouVersion - Yorùbá',
            description: 'YouVersion Bible with Yorùbá translations',
            type: 'App',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-smartphone-line',
            color: 'from-indigo-600 to-purple-700',
            category: 'Bible',
            languages: ['Yorùbá'],
            platforms: ['Android', 'iOS', 'Web'],
            link: 'https://www.bible.com/'
          }
        ]
      },
      {
        name: 'Igbo Bible Apps',
        description: 'Bible applications in Igbo language',
        icon: 'ri-book-line',
        color: 'from-orange-600 to-orange-700',
        resources: [
          {
            name: 'Igbo Bible App',
            description: 'Bible in Igbo language with audio',
            type: 'App',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-book-line',
            color: 'from-orange-600 to-orange-700',
            category: 'Bible',
            languages: ['Igbo'],
            platforms: ['Android', 'iOS'],
            link: 'https://play.google.com/store/apps/details?id=com.igbobible'
          },
          {
            name: 'YouVersion - Igbo',
            description: 'YouVersion Bible with Igbo translations',
            type: 'App',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-smartphone-line',
            color: 'from-amber-600 to-orange-700',
            category: 'Bible',
            languages: ['Igbo'],
            platforms: ['Android', 'iOS', 'Web'],
            link: 'https://www.bible.com/'
          }
        ]
      },
      {
        name: 'Efik Bible Apps',
        description: 'Bible applications in Efik language',
        icon: 'ri-book-line',
        color: 'from-pink-600 to-pink-700',
        resources: [
          {
            name: 'Efik Bible Resources',
            description: 'Bible and study materials in Efik language',
            type: 'Web',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-book-open-line',
            color: 'from-pink-600 to-pink-700',
            category: 'Bible',
            languages: ['Efik'],
            platforms: ['Web'],
            link: 'https://www.biblegateway.com/',
            note: 'Includes Tiv, Ibibio, and other languages'
          }
        ]
      },
      {
        name: 'Hausa Bible Apps',
        description: 'Bible applications in Hausa language',
        icon: 'ri-book-line',
        color: 'from-cyan-600 to-cyan-700',
        resources: [
          {
            name: 'Hausa Bible App',
            description: 'Complete Hausa Bible with study tools',
            type: 'App',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-book-line',
            color: 'from-cyan-600 to-cyan-700',
            category: 'Bible',
            languages: ['Hausa'],
            platforms: ['Android', 'iOS'],
            link: 'https://play.google.com/store/apps/details?id=com.hausabible'
          },
          {
            name: 'YouVersion - Hausa',
            description: 'YouVersion Bible with Hausa translations',
            type: 'App',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-smartphone-line',
            color: 'from-teal-600 to-cyan-700',
            category: 'Bible',
            languages: ['Hausa'],
            platforms: ['Android', 'iOS', 'Web'],
            link: 'https://www.bible.com/'
          }
        ]
      },
      {
        name: 'Nigerian Pidgin Bible Apps',
        description: 'Bible applications in Nigerian Pidgin',
        icon: 'ri-book-line',
        color: 'from-rose-600 to-rose-700',
        resources: [
          {
            name: 'Nigerian Pidgin Bible',
            description: 'Bible in Nigerian Pidgin English',
            type: 'Web',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-global-line',
            color: 'from-rose-600 to-rose-700',
            category: 'Bible',
            languages: ['Nigerian Pidgin'],
            platforms: ['Web'],
            link: 'https://www.bible.com/versions/476-pcm-pidgin-bible'
          }
        ]
      },
      {
        name: 'Other Local Languages',
        description: 'Bible resources in additional Nigerian languages',
        icon: 'ri-translate-2',
        color: 'from-teal-600 to-emerald-700',
        resources: [
          {
            name: 'Multi-Language Bible Gateway',
            description: 'Access to Bible in various Nigerian languages',
            type: 'Web',
            source: 'Official Bible Platform',
            accessMode: 'External Link',
            icon: 'ri-translate-2',
            color: 'from-teal-600 to-emerald-700',
            category: 'Bible',
            languages: ['Multiple Languages'],
            platforms: ['Web'],
            link: 'https://www.biblegateway.com/',
            note: 'Includes Tiv, Ibibio, and other languages'
          }
        ]
      }
    ],
    Digital: [
      {
        name: 'Membership & Records Systems',
        description: 'Official platforms for membership management',
        icon: 'ri-database-2-line',
        color: 'from-blue-600 to-indigo-700',
        resources: [
          {
            name: 'Church Membership System',
            description: 'Official platform for membership records and management',
            type: 'Web',
            source: 'Conference System',
            accessMode: 'External Link',
            icon: 'ri-database-2-line',
            color: 'from-blue-600 to-indigo-700',
            category: 'Digital',
            note: 'External Church Platform'
          }
        ]
      },
      {
        name: 'Education & Academic Portals',
        description: 'Access to SDA educational resources',
        icon: 'ri-graduation-cap-line',
        color: 'from-emerald-600 to-teal-700',
        resources: [
          {
            name: 'Adventist Education Portal',
            description: 'Access to SDA educational resources and systems',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-graduation-cap-line',
            color: 'from-emerald-600 to-teal-700',
            category: 'Digital',
            link: 'https://education.adventist.org/',
            note: 'External Church Platform'
          }
        ]
      },
      {
        name: 'Publishing House Platforms',
        description: 'Official church literature and publishing',
        icon: 'ri-book-2-line',
        color: 'from-purple-600 to-purple-700',
        resources: [
          {
            name: 'Adventist Publishing Network',
            description: 'Official church literature and publishing resources',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-book-2-line',
            color: 'from-purple-600 to-purple-700',
            category: 'Digital',
            link: 'https://www.adventistbookcenter.com/',
            note: 'External Church Platform'
          }
        ]
      },
      {
        name: 'Health Ministries Platforms',
        description: 'Resources for health and wellness ministry',
        icon: 'ri-heart-pulse-line',
        color: 'from-rose-600 to-rose-700',
        resources: [
          {
            name: 'Health Ministries Portal',
            description: 'Resources for health and wellness ministry',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-heart-pulse-line',
            color: 'from-rose-600 to-rose-700',
            category: 'Digital',
            link: 'https://www.adventisthealthministries.org/',
            note: 'External Church Platform'
          }
        ]
      },
      {
        name: 'Media & Communication Portals',
        description: 'Official church media and communication platforms',
        icon: 'ri-broadcast-line',
        color: 'from-indigo-600 to-purple-700',
        resources: [
          {
            name: 'Adventist News Network',
            description: 'Official news service of the Seventh-day Adventist Church',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-newspaper-line',
            color: 'from-indigo-600 to-purple-700',
            category: 'Digital',
            link: 'https://www.adventist.news/',
            note: 'External Church Platform'
          },
          {
            name: 'Hope Channel',
            description: 'Official television network of the Seventh-day Adventist Church',
            type: 'Web',
            source: 'Global SDA Church',
            accessMode: 'External Link',
            icon: 'ri-tv-line',
            color: 'from-blue-600 to-indigo-700',
            category: 'Digital',
            link: 'https://www.hopechannel.com/',
            note: 'External Church Platform'
          }
        ]
      }
    ],
    Media: [
      {
        name: 'Official Church Logos',
        description: 'Approved church logos and branding elements',
        icon: 'ri-image-line',
        color: 'from-blue-600 to-indigo-700',
        resources: [
          {
            name: 'Global Church Logo Package',
            description: 'Official Seventh-day Adventist Church logos',
            type: 'ZIP',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-image-line',
            color: 'from-blue-600 to-indigo-700',
            category: 'Media',
            formats: ['PNG', 'SVG', 'EPS'],
            note: 'For official church use only'
          },
          {
            name: 'Division Logo Package',
            description: 'Division-specific logos and branding',
            type: 'ZIP',
            source: 'Division',
            accessMode: 'Download',
            icon: 'ri-image-2-line',
            color: 'from-purple-600 to-purple-700',
            category: 'Media',
            formats: ['PNG', 'SVG'],
            note: 'For official church use only'
          }
        ]
      },
      {
        name: 'Branding & Usage Guidelines',
        description: 'Official guidelines for using church branding',
        icon: 'ri-palette-line',
        color: 'from-emerald-600 to-teal-700',
        resources: [
          {
            name: 'Brand Identity Guidelines',
            description: 'Official guidelines for church branding and identity',
            type: 'PDF',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-palette-line',
            color: 'from-emerald-600 to-teal-700',
            category: 'Media',
            note: 'For official church use only'
          }
        ]
      },
      {
        name: 'Evangelism & Campaign Media Kits',
        description: 'Media resources for evangelistic campaigns',
        icon: 'ri-megaphone-line',
        color: 'from-orange-600 to-amber-700',
        resources: [
          {
            name: 'Evangelism Media Toolkit',
            description: 'Graphics and media for evangelistic programs',
            type: 'ZIP',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-megaphone-line',
            color: 'from-orange-600 to-amber-700',
            category: 'Media',
            formats: ['PSD', 'AI', 'PNG']
          }
        ]
      },
      {
        name: 'Public Communication Toolkits',
        description: 'Resources for church communication and PR',
        icon: 'ri-chat-3-line',
        color: 'from-cyan-600 to-blue-700',
        resources: [
          {
            name: 'Communication Toolkit',
            description: 'Templates and resources for church communications',
            type: 'ZIP',
            source: 'Global SDA Church',
            accessMode: 'Download',
            icon: 'ri-chat-3-line',
            color: 'from-cyan-600 to-blue-700',
            category: 'Media',
            formats: ['DOCX', 'PPTX', 'PDF']
          }
        ]
      }
    ]
  };

  // Get all resources for counting
  const getAllResources = () => {
    const allRes: any[] = [];
    Object.values(subcategories).forEach((subs: any) => {
      subs.forEach((sub: any) => {
        allRes.push(...sub.resources);
      });
    });
    return allRes;
  };

  const allResources = getAllResources();

  // Function to get category count
  const getCategoryCount = (category: string) => {
    if (category === 'All') {
      return allResources.length;
    }
    return allResources.filter(r => r.category === category).length;
  };

  // Get current view content
  const getCurrentContent = () => {
    if (selectedCategory === 'All') {
      // Show all subcategories from all categories
      return Object.entries(subcategories).map(([catKey, subs]) => ({
        categoryKey: catKey,
        subcategories: subs
      }));
    } else {
      // Show subcategories for selected category
      const subs = subcategories[selectedCategory as keyof typeof subcategories];
      return subs ? [{ categoryKey: selectedCategory, subcategories: subs }] : [];
    }
  };

  // Enhanced filtering with search
  const filteredContent = useMemo(() => {
    let content = getCurrentContent();

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      content = content.map(cat => ({
        ...cat,
        subcategories: cat.subcategories.map((sub: any) => ({
          ...sub,
          resources: sub.resources.filter((resource: any) => 
            resource.name.toLowerCase().includes(query) ||
            resource.description.toLowerCase().includes(query) ||
            resource.category.toLowerCase().includes(query) ||
            (resource.formats && resource.formats.some((f: string) => f.toLowerCase().includes(query))) ||
            (resource.languages && resource.languages.some((l: string) => l.toLowerCase().includes(query)))
          )
        })).filter((sub: any) => sub.resources.length > 0)
      })).filter(cat => cat.subcategories.length > 0);
    }

    return content;
  }, [selectedCategory, searchQuery]);

  // Count total filtered resources
  const getTotalFilteredResources = () => {
    let count = 0;
    filteredContent.forEach(cat => {
      cat.subcategories.forEach((sub: any) => {
        count += sub.resources.length;
      });
    });
    return count;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - Minimal institutional design */}
      <div className="relative bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-white">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              Resources Hub
            </h1>
            <p className="text-base sm:text-lg text-white/90 mb-2">
              Centralized access to official Seventh-day Adventist Church resources
            </p>
            <p className="text-sm text-white/70">
              Study, worship, leadership, stewardship, and governance materials—curated for easy access.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-11 pr-11 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 shadow-sm border border-gray-300 bg-white hover:border-emerald-400 transition-colors duration-200"
            />
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors duration-200"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            )}
          </div>
        </div>

        {/* Category Filters - Horizontal scrollable, single line, mobile optimized */}
        <div className="mb-6 -mx-4 sm:mx-0">
          <div className="overflow-x-auto scrollbar-hide px-4 sm:px-0">
            <div className="flex items-center gap-2 min-w-max pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => {
                    setSelectedCategory(cat.value);
                    setSelectedSubcategory(null);
                  }}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 ${
                    selectedCategory === cat.value
                      ? 'bg-emerald-700 text-white shadow-sm hover:bg-emerald-800 hover:shadow-md transform hover:scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-300 border border-gray-200 hover:shadow-sm'
                  }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-xs ${
                    selectedCategory === cat.value ? 'text-white/80' : 'text-gray-500'
                  }`}>
                    ({getCategoryCount(cat.value)})
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-5 text-center">
          <p className="text-xs sm:text-sm text-gray-600">
            Showing <strong className="text-gray-900">{getTotalFilteredResources()}</strong> of <strong className="text-gray-900">{allResources.length}</strong> resources
            {searchQuery && <span className="text-emerald-700 font-medium"> matching "{searchQuery}"</span>}
          </p>
        </div>

        {/* No Results State */}
        {filteredContent.length === 0 && (
          <div className="text-center py-12 sm:py-16 bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-sm border border-gray-200">
            <i className="ri-search-line text-4xl sm:text-5xl text-gray-300 mb-3"></i>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 px-4">No resources found</h3>
            <p className="text-xs sm:text-sm text-gray-600 px-4">
              {searchQuery 
                ? `No resources match "${searchQuery}". Try different keywords.`
                : 'Try selecting a different category or adjusting your search.'}
            </p>
          </div>
        )}

        {/* Subcategories and Resources */}
        {filteredContent.length > 0 && (
          <div className="space-y-8 sm:space-y-10">
            {filteredContent.map((cat, catIdx) => (
              <div key={catIdx}>
                {cat.subcategories.map((subcategory: any, subIdx: number) => (
                  <div key={subIdx} className="mb-8 sm:mb-10">
                    {/* Subcategory Header - Minimal institutional style */}
                    <div className="mb-4 sm:mb-5 bg-gradient-to-r from-gray-50 to-white p-3 sm:p-4 rounded-lg border border-gray-100">
                      <div className="flex items-start sm:items-center gap-3 mb-2">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                          <i className={`${subcategory.icon} text-base sm:text-lg text-gray-600`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                            {subcategory.name}
                          </h2>
                          <p className="text-xs sm:text-sm text-gray-600">{subcategory.description}</p>
                        </div>
                      </div>
                    </div>

                    {/* Resources Grid - Responsive */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                      {subcategory.resources.map((resource: any, resIdx: number) => (
                        <ResourceCard key={resIdx} resource={resource} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
